import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, Mail, Smartphone, BarChart3, Cpu, Play, Menu, X, Check, Send } from 'lucide-react';
import { getUTMParams, trackLead, trackEvent } from './Analytics';

// ════════════════════════════════════════════════════════════════════════════════
// VERDENSKLASSE ULTRA-MINIMALIST FUTURISTISK WEBSITE - FULLY RESPONSIVE
// ════════════════════════════════════════════════════════════════════════════════

const smoothEase = [0.43, 0.13, 0.23, 0.96];

// ════════════════════════════════════════════════════════════════════════════════
// RESPONSIVE NAV - Hidden until scroll
// ════════════════════════════════════════════════════════════════════════════════
function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      // Add background after scrolling 50px
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav 
      initial={{ y: 0, opacity: 1 }}
      animate={{ 
        y: 0, 
        opacity: 1 
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '16px clamp(20px, 5vw, 60px)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 100,
        background: scrolled ? 'rgba(0,0,0,0.9)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(255,255,255,0.05)' : 'none',
        transition: 'background 0.3s, backdrop-filter 0.3s, border-bottom 0.3s',
      }}
    >
      <a href="/" className="logo-text" style={{ 
        fontSize: '24px', 
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        fontWeight: '100', 
        color: '#fff', 
        textDecoration: 'none',
        letterSpacing: '-0.02em',
        WebkitFontSmoothing: 'antialiased',
      }}>
        a.
      </a>

      {/* Desktop nav */}
      <div style={{
        display: 'flex',
        gap: '32px',
        alignItems: 'center',
      }} className="desktop-nav">
        <a href="#tjenester" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none', transition: 'color 0.2s' }}>Tjenester</a>
        <a href="#teknologi" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none', transition: 'color 0.2s' }}>Teknologi</a>
        <a href="#kontakt" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none', transition: 'color 0.2s' }}>Kontakt</a>
      </div>

      {/* Mobile menu button */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="mobile-menu-btn"
        style={{
          background: 'none',
          border: 'none',
          color: '#fff',
          cursor: 'pointer',
          padding: '8px',
          display: 'none',
        }}
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile nav overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mobile-nav-overlay"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.98)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '32px',
              zIndex: 99,
            }}
          >
            <a href="#tjenester" onClick={() => setIsOpen(false)} style={{ fontSize: '24px', color: '#fff', textDecoration: 'none' }}>Tjenester</a>
            <a href="#teknologi" onClick={() => setIsOpen(false)} style={{ fontSize: '24px', color: '#fff', textDecoration: 'none' }}>Teknologi</a>
            <a href="#kontakt" onClick={() => setIsOpen(false)} style={{ fontSize: '24px', color: '#fff', textDecoration: 'none' }}>Kontakt</a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// HERO SECTION
// ════════════════════════════════════════════════════════════════════════════════
function HeroSection() {
  return (
    <section style={{
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      overflow: 'hidden',
      background: '#000',
      padding: '20px',
    }}>
      {/* Logo with subtle glow and elegant float animation */}
      <div style={{ position: 'relative' }}>
        {/* Breathing glow effect */}
        <motion.div
          animate={{ 
            opacity: [0.15, 0.35, 0.15],
            scale: [1, 1.1, 1],
          }}
          transition={{ 
            duration: 4, 
            repeat: Infinity, 
            ease: 'easeInOut' 
          }}
          style={{
            position: 'absolute',
            inset: '-80px',
            background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)',
            filter: 'blur(50px)',
            pointerEvents: 'none',
          }}
        />
        
        {/* The "a." logo with elegant float */}
        <motion.span
          className="logo-text"
          initial={{ opacity: 0, y: 30 }}
          animate={{ 
            opacity: 1, 
            y: [0, -8, 0],
          }}
          transition={{ 
            opacity: { duration: 1.5, delay: 0.2, ease: smoothEase },
            y: { 
              duration: 4, 
              repeat: Infinity, 
              ease: 'easeInOut',
              delay: 1.5,
            }
          }}
          style={{
            fontSize: 'clamp(80px, 25vw, 260px)',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            fontWeight: '100',
            letterSpacing: '-0.06em',
            color: '#fff',
            lineHeight: 0.75,
            display: 'block',
            position: 'relative',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          }}
        >
          a.
        </motion.span>
      </div>

      {/* Brand */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 0.6, ease: smoothEase }}
        style={{
          fontSize: 'clamp(12px, 3vw, 18px)',
          fontWeight: '400',
          letterSpacing: '0.35em',
          color: 'rgba(255,255,255,0.5)',
          marginTop: 'clamp(20px, 5vh, 48px)',
        }}
      >
        autonomous
      </motion.p>

      {/* Tagline - LARGER */}
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 0.9, ease: smoothEase }}
        style={{
          fontSize: 'clamp(22px, 5vw, 38px)',
          fontWeight: '300',
          color: 'rgba(255,255,255,0.6)',
          marginTop: 'clamp(32px, 8vh, 100px)',
          letterSpacing: '0.01em',
          textAlign: 'center',
        }}
      >
        Vi syssler med AI.
      </motion.h1>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// SERVICES SECTION - FULLY RESPONSIVE
// ════════════════════════════════════════════════════════════════════════════════
function ServicesSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-50px' });
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      number: '01',
      title: 'App Utvikling',
      description: 'Native og cross-platform apper som brukerne elsker. Bygget for hastighet, skalerbarhet og perfekt brukeropplevelse.',
      icon: Smartphone,
      preview: 'app',
    },
    {
      number: '02', 
      title: 'Web & Dashboard',
      description: 'Skalerbare plattformer med sanntidsdata og innsikt. Fra MVP til enterprise-løsninger.',
      icon: BarChart3,
      preview: 'dashboard',
    },
    {
      number: '03',
      title: 'AI Integrasjon',
      description: 'Smarte funksjoner som automatiserer og optimaliserer. GPT, Vision, Custom Models.',
      icon: Cpu,
      preview: 'ai',
    }
  ];

  return (
    <section
      id="tjenester"
      ref={sectionRef}
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        padding: 'clamp(60px, 10vh, 100px) clamp(20px, 5vw, 100px)',
        position: 'relative',
        background: '#000',
      }}
    >
      <div className="services-container" style={{
        width: '100%',
        maxWidth: '1400px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '40px',
        alignItems: 'center',
      }}>
        {/* Services List */}
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            style={{
              fontSize: '11px',
              fontWeight: '500',
              color: 'rgba(255,255,255,0.35)',
              letterSpacing: '0.2em',
              marginBottom: 'clamp(24px, 5vh, 48px)',
              textTransform: 'uppercase',
            }}
          >
            Våre Tjenester
          </motion.p>

          {services.map((service, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }}
              onMouseEnter={() => setActiveService(i)}
              onClick={() => setActiveService(i)}
              style={{
                padding: 'clamp(20px, 4vw, 32px) 0',
                borderBottom: i < services.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              {/* Active indicator line - hidden on mobile */}
              <motion.div
                className="active-indicator"
                initial={{ width: 0 }}
                animate={{ width: activeService === i ? '40px' : 0 }}
                transition={{ duration: 0.4, ease: smoothEase }}
                style={{
                  position: 'absolute',
                  left: '-24px',
                  top: '50%',
                  height: '1px',
                  background: '#fff',
                }}
              />

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{
                  fontSize: '11px',
                  color: activeService === i ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)',
                  fontWeight: '500',
                  fontFamily: 'monospace',
                  transition: 'color 0.3s',
                }}>
                  {service.number}
                </span>
                <h3 style={{
                  fontSize: 'clamp(24px, 5vw, 42px)',
                  fontWeight: '300',
                  color: activeService === i ? '#fff' : 'rgba(255,255,255,0.35)',
                  letterSpacing: '-0.02em',
                  transition: 'color 0.3s',
                  margin: 0,
                }}>
                  {service.title}
                </h3>
              </div>

              <AnimatePresence>
                {activeService === i && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      fontSize: 'clamp(13px, 2vw, 14px)',
                      color: 'rgba(255,255,255,0.5)',
                      lineHeight: 1.7,
                      marginLeft: 'clamp(0px, 3vw, 40px)',
                      overflow: 'hidden',
                      paddingRight: '20px',
                    }}
                  >
                    {service.description}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Interactive Preview - Desktop only */}
        <motion.div
          className="preview-container"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          style={{
            height: 'clamp(500px, 65vh, 620px)',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.01) 100%)',
            borderRadius: '24px',
            border: '1px solid rgba(255,255,255,0.06)',
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AnimatePresence mode="wait">
            {activeService === 0 && (
              <motion.div
                key="app"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'clamp(20px, 4vw, 40px)',
                }}
              >
                <AppPreview />
              </motion.div>
            )}

            {activeService === 1 && (
              <motion.div
                key="dashboard"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  padding: 'clamp(16px, 3vw, 24px)',
                }}
              >
                <DashboardPreview />
              </motion.div>
            )}

            {activeService === 2 && (
              <motion.div
                key="ai"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'clamp(20px, 4vw, 40px)',
                }}
              >
                <AIPreview />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// PREVIEW COMPONENTS - ULTRA MINIMALIST (BLACK/WHITE/GRAY ONLY)
// ════════════════════════════════════════════════════════════════════════════════

function AppPreview() {
  return (
    <div style={{
      width: '100%',
      maxWidth: '200px',
      margin: '0 auto',
      aspectRatio: '9/17',
      background: '#111',
      borderRadius: '24px',
      padding: '5px',
      boxShadow: '0 30px 60px rgba(0,0,0,0.4)',
      border: '1px solid rgba(255,255,255,0.08)',
    }}>
      <div style={{
        width: '100%',
        height: '100%',
        background: '#0a0a0a',
        borderRadius: '20px',
        overflow: 'hidden',
        position: 'relative',
      }}>
        {/* Dynamic Island */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          padding: '6px 0 4px',
        }}>
          <div style={{
            width: '50px',
            height: '16px',
            background: '#000',
            borderRadius: '10px',
          }} />
        </div>

        {/* App Header */}
        <div style={{ padding: '8px 12px 6px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: '10px',
          }}>
            <div>
              <p style={{ fontSize: '7px', color: 'rgba(255,255,255,0.4)', marginBottom: '1px' }}>God morgen</p>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#fff' }}>Daniel</p>
            </div>
            <div style={{ 
              width: '24px', 
              height: '24px', 
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '7px',
            }} />
          </div>
        </div>

        {/* Stats Cards */}
        <div style={{ padding: '0 8px', display: 'flex', gap: '5px', marginBottom: '8px' }}>
          <div style={{
            flex: 1,
            background: 'rgba(255,255,255,0.08)',
            borderRadius: '8px',
            padding: '8px',
          }}>
            <p style={{ fontSize: '6px', color: 'rgba(255,255,255,0.5)', marginBottom: '2px' }}>Aktive</p>
            <p style={{ fontSize: '14px', fontWeight: '700', color: '#fff' }}>847</p>
          </div>
          <div style={{
            flex: 1,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '8px',
            padding: '8px',
          }}>
            <p style={{ fontSize: '6px', color: 'rgba(255,255,255,0.4)', marginBottom: '2px' }}>Vekst</p>
            <p style={{ fontSize: '14px', fontWeight: '700', color: 'rgba(255,255,255,0.9)' }}>+23%</p>
          </div>
        </div>
        
        {/* List Items */}
        <div style={{ padding: '0 8px' }}>
          {[
            { title: 'Analytics', subtitle: 'Se statistikk' },
            { title: 'Aktivitet', subtitle: '12 nye' },
            { title: 'Meldinger', subtitle: '3 uleste' },
          ].map((item, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + i * 0.1 }}
              style={{
                background: 'rgba(255,255,255,0.03)',
                borderRadius: '7px',
                padding: '7px 8px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                border: '1px solid rgba(255,255,255,0.04)',
              }}
            >
              <div style={{ 
                width: '20px', 
                height: '20px', 
                background: 'rgba(255,255,255,0.08)',
                borderRadius: '5px',
                flexShrink: 0,
              }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '9px', fontWeight: '500', color: '#fff', marginBottom: '1px' }}>{item.title}</p>
                <p style={{ fontSize: '7px', color: 'rgba(255,255,255,0.4)' }}>{item.subtitle}</p>
              </div>
              <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: '9px' }}>→</div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Nav */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '6px 16px 12px',
          display: 'flex',
          justifyContent: 'space-around',
          background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.95) 100%)',
        }}>
          {[1,2,3,4,5].map((_, i) => (
            <div key={i} style={{
              width: '20px',
              height: '20px',
              borderRadius: '5px',
              background: i === 0 ? 'rgba(255,255,255,0.15)' : 'transparent',
              border: i !== 0 ? '1px solid rgba(255,255,255,0.08)' : 'none',
            }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function DashboardPreview() {
  return (
    <div style={{ 
      width: '100%',
      height: '100%',
      display: 'flex', 
      gap: '10px',
      padding: '6px',
    }}>
      {/* Sidebar - MONOCHROME */}
      <div style={{ 
        width: '120px', 
        background: 'rgba(255,255,255,0.02)', 
        borderRadius: '10px', 
        padding: '14px 10px',
        border: '1px solid rgba(255,255,255,0.04)',
        flexShrink: 0,
      }}>
        <div style={{ 
          fontSize: '12px', 
          fontWeight: '600', 
          color: '#fff',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          <div style={{ 
            width: '20px', 
            height: '20px', 
            background: 'rgba(255,255,255,0.15)', 
            borderRadius: '5px',
          }} />
          <span>acme</span>
        </div>

        {['Dashboard', 'Analytics', 'Kunder', 'Rapporter'].map((item, i) => (
          <div key={item} style={{
            padding: '8px 10px',
            borderRadius: '6px',
            marginBottom: '3px',
            background: i === 0 ? 'rgba(255,255,255,0.08)' : 'transparent',
            color: i === 0 ? '#fff' : 'rgba(255,255,255,0.35)',
            fontSize: '10px',
            fontWeight: i === 0 ? '500' : '400',
          }}>
            {item}
          </div>
        ))}
      </div>
      
      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {/* Stats Row - MONOCHROME */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
          {[
            { label: 'Revenue', value: 'kr 2.4M', change: '+12%' },
            { label: 'Users', value: '18,472', change: '+8%' },
            { label: 'Conv.', value: '3.24%', change: '+2%' },
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              style={{
                background: 'rgba(255,255,255,0.02)',
                borderRadius: '10px',
                padding: '12px',
                border: '1px solid rgba(255,255,255,0.04)',
              }}
            >
              <p style={{ fontSize: '8px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</p>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <p style={{ fontSize: '16px', fontWeight: '600', color: '#fff' }}>{stat.value}</p>
                <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)' }}>{stat.change}</span>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Chart Area - MONOCHROME */}
        <div style={{
          flex: 1,
          background: 'rgba(255,255,255,0.02)',
          borderRadius: '10px',
          padding: '14px',
          border: '1px solid rgba(255,255,255,0.04)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <p style={{ fontSize: '11px', fontWeight: '500', color: '#fff' }}>Revenue</p>
            <div style={{ display: 'flex', gap: '3px' }}>
              {['1W', '1M', '1Y'].map((period, i) => (
                <span key={period} style={{
                  padding: '3px 8px',
                  borderRadius: '4px',
                  fontSize: '9px',
                  background: i === 1 ? 'rgba(255,255,255,0.1)' : 'transparent',
                  color: i === 1 ? '#fff' : 'rgba(255,255,255,0.35)',
                }}>
                  {period}
                </span>
              ))}
            </div>
          </div>
          
          {/* Chart - MONOCHROME */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '5px', height: 'calc(100% - 35px)' }}>
            {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 100].map((height, i) => (
              <motion.div
                key={i}
                initial={{ height: 0 }}
                animate={{ height: `${height}%` }}
                transition={{ duration: 0.8, delay: 0.2 + i * 0.05 }}
                style={{
                  flex: 1,
                  background: i === 11 
                    ? 'rgba(255,255,255,0.9)'
                    : 'rgba(255,255,255,0.08)',
                  borderRadius: '3px',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AIPreview() {
  const [currentMsg, setCurrentMsg] = useState(0);
  
  const messages = [
    { type: 'user', text: 'Analyser Q4 salgsdata' },
    { type: 'ai', text: 'Q4 viser 23% vekst. Hoveddriver er Enterprise med 847 nye kunder.' },
    { type: 'user', text: 'Generer rapport' },
  ];

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentMsg(prev => (prev + 1) % (messages.length + 1));
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div style={{ 
      width: '100%', 
      maxWidth: '380px',
      margin: '0 auto',
      padding: '16px',
    }}>
      {/* AI Header - MONOCHROME */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '12px',
          background: 'rgba(255,255,255,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(255,255,255,0.1)',
        }}>
          <Cpu size={20} color="rgba(255,255,255,0.8)" />
        </div>
        <div>
          <p style={{ fontSize: '13px', fontWeight: '600', color: '#fff', margin: 0 }}>AI Assistant</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '2px' }}>
            <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'rgba(255,255,255,0.5)' }} />
            <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', margin: 0 }}>Online</p>
          </div>
        </div>
      </div>

      {/* Messages - MONOCHROME */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {messages.map((msg, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 12 }}
            animate={{ 
              opacity: i <= currentMsg ? 1 : 0.15, 
              y: i <= currentMsg ? 0 : 12,
            }}
            transition={{ duration: 0.4 }}
            style={{
              display: 'flex',
              justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <div style={{
              padding: '12px 16px',
              borderRadius: msg.type === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              background: msg.type === 'user' 
                ? 'rgba(255,255,255,0.95)' 
                : 'rgba(255,255,255,0.05)',
              maxWidth: '85%',
              border: msg.type === 'ai' ? '1px solid rgba(255,255,255,0.06)' : 'none',
            }}>
              <p style={{
                margin: 0,
                fontSize: '12px',
                color: msg.type === 'user' ? '#000' : 'rgba(255,255,255,0.8)',
                lineHeight: 1.6,
              }}>
                {msg.text}
              </p>
            </div>
          </motion.div>
        ))}
        
        {/* Typing indicator - MONOCHROME */}
        {currentMsg >= messages.length && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ 
              display: 'flex', 
              gap: '4px', 
              padding: '14px 16px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '16px 16px 16px 4px',
              width: 'fit-content',
            }}
          >
            {[0,1,2].map(i => (
              <motion.div
                key={i}
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.12 }}
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.6)',
                }}
              />
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// VIDEO SECTION - WORLD CLASS DESIGN
// ════════════════════════════════════════════════════════════════════════════════
function VideoSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-50px' });
  const [isMuted, setIsMuted] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const videoEl = useRef(null);

  const handlePlayWithSound = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const video = videoEl.current;
    if (!video) return;

    video.currentTime = 0;
    video.muted = false;
    setIsMuted(false);
  };

  const features = [
    { title: 'Autonom utvikling', desc: 'Kode som skriver seg selv' },
    { title: 'Intelligent testing', desc: 'Finner bugs før de oppstår' },
    { title: 'Kontinuerlig læring', desc: 'Blir smartere hver dag' },
  ];

  return (
    <section
      id="teknologi"
      ref={sectionRef}
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        padding: 'clamp(60px, 10vh, 100px) clamp(24px, 5vw, 80px)',
        background: '#000',
        position: 'relative',
      }}
    >
      <div style={{
        width: '100%',
        maxWidth: '1300px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'clamp(40px, 6vw, 100px)',
        alignItems: 'center',
      }} className="video-grid">
        
        {/* Left - Large Video */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, ease: smoothEase }}
          style={{ position: 'relative' }}
        >
          {/* Ambient glow */}
          <motion.div
            animate={{ 
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '120%',
              height: '120%',
              background: 'radial-gradient(ellipse, rgba(255,255,255,0.08) 0%, transparent 60%)',
              filter: 'blur(60px)',
              pointerEvents: 'none',
            }}
          />

          {/* Video frame */}
          <div 
            style={{
              position: 'relative',
              borderRadius: '24px',
              overflow: 'hidden',
              boxShadow: isHovered 
                ? '0 50px 100px rgba(0,0,0,0.5)'
                : '0 30px 70px rgba(0,0,0,0.4)',
              background: '#0a0a0a',
              transform: isHovered ? 'scale(1.01)' : 'scale(1)',
              transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
              aspectRatio: '9/16',
              maxHeight: '80vh',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <video
              ref={videoEl}
              autoPlay
              muted
              loop
              playsInline
              controls={!isMuted}
              style={{ 
                width: '100%', 
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
              src="/autonomous_with_audio.mp4"
            />

            {/* Play button - CENTER */}
            {isMuted && (
              <button
                type="button"
                onClick={handlePlayWithSound}
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.15)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <motion.span 
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.95)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  }}
                >
                  <Play size={24} fill="#000" color="#000" style={{ marginLeft: '3px' }} />
                </motion.span>
              </button>
            )}
          </div>
        </motion.div>

        {/* Right - Content */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.15, ease: smoothEase }}
        >
          {/* Label with line */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
            <div style={{ width: '40px', height: '1px', background: 'rgba(255,255,255,0.3)' }} />
            <span style={{
              fontSize: '11px',
              fontWeight: '500',
              color: 'rgba(255,255,255,0.4)',
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
            }}>
              Vår Teknologi
            </span>
          </div>

          <h2 style={{
            fontSize: 'clamp(36px, 4.5vw, 56px)',
            fontWeight: '300',
            color: '#fff',
            letterSpacing: '-0.03em',
            lineHeight: 1.1,
            marginBottom: '12px',
          }}>
            AI Agenter som
          </h2>
          
          <h2 style={{
            fontSize: 'clamp(36px, 4.5vw, 56px)',
            fontWeight: '300',
            fontStyle: 'italic',
            color: 'rgba(255,255,255,0.4)',
            letterSpacing: '-0.03em',
            lineHeight: 1.1,
            marginBottom: '32px',
          }}>
            bygger fremtiden
          </h2>

          <p style={{
            fontSize: 'clamp(14px, 1.3vw, 16px)',
            color: 'rgba(255,255,255,0.45)',
            lineHeight: 1.8,
            marginBottom: '40px',
            maxWidth: '440px',
          }}>
            Vi bruker avanserte AI-agenter som arbeider autonomt for å utvikle, teste og optimalisere løsninger. 
            Våre agenter lærer kontinuerlig og tilpasser seg dine behov – døgnet rundt.
          </p>

          {/* Features with descriptions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '40px' }}>
            {features.map((feature, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '16px',
                }}
              >
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#fff',
                  flexShrink: 0,
                }} />
                <span style={{
                  fontSize: '15px',
                  fontWeight: '500',
                  color: '#fff',
                }}>
                  {feature.title}
                </span>
                <span style={{
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.4)',
                  fontStyle: 'italic',
                }}>
                  {feature.desc}
                </span>
              </motion.div>
            ))}
          </div>

          {/* CTA Button */}
          <motion.a
            href="#kontakt"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.7 }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              padding: '16px 32px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '14px',
              fontWeight: '500',
              textDecoration: 'none',
              transition: 'all 0.3s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
            }}
          >
            Utforsk teknologien
            <ArrowUpRight size={16} />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// TECH STACK SECTION
// ════════════════════════════════════════════════════════════════════════════════
function TechSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-50px' });

  return (
    <section
      ref={sectionRef}
      style={{
        padding: 'clamp(60px, 10vh, 100px) clamp(20px, 5vw, 100px)',
        background: '#000',
        borderTop: '1px solid rgba(255,255,255,0.04)',
      }}
    >
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        textAlign: 'center',
      }}>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          style={{
            fontSize: '11px',
            fontWeight: '500',
            color: 'rgba(255,255,255,0.35)',
            letterSpacing: '0.2em',
            marginBottom: '24px',
            textTransform: 'uppercase',
          }}
        >
          Tech Stack
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.1 }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 'clamp(24px, 5vw, 48px)',
            flexWrap: 'wrap',
          }}
        >
          {['React', 'Node.js', 'Python', 'GPT-4', 'AWS', 'Vercel'].map((tech, i) => (
            <motion.span
              key={tech}
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.2 + i * 0.08 }}
              style={{
                fontSize: 'clamp(12px, 2vw, 14px)',
                color: 'rgba(255,255,255,0.25)',
                letterSpacing: '0.1em',
                fontWeight: '500',
              }}
            >
              {tech}
            </motion.span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// TEAM SECTION
// ════════════════════════════════════════════════════════════════════════════════
const MARTIN_IMAGE = "https://customer-assets.emergentagent.com/job_win95-retro/artifacts/1txzsvha_603a4205317945628ebfbb5a_Martin.jpg";
const ERIK_IMAGE = "https://customer-assets.emergentagent.com/job_win95-retro/artifacts/ri2p47ex_image.png";

function TeamSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-50px' });

  const team = [
    {
      name: 'Martin Kviteberg',
      role: 'Daglig leder & Gründer',
      image: MARTIN_IMAGE,
      tags: ['Forretningsutvikling', 'Produkt'],
      bio: '10+ års erfaring som Direktør for Forretningsutvikling i Adonis HR, et ledende norsk selskap innen HR- og lønnssystemer. Seriegrunder med lidenskap for å bygge selskaper fra bunn.',
    },
    {
      name: 'Erik Hoffmann-Dahl',
      role: 'Partner & Legal Counsel',
      image: ERIK_IMAGE,
      tags: ['Forretningsjus', 'M&A'],
      bio: 'Partner og daglig leder i Hoffmann Thinn Advokatfirma AS i Bergen. Spesialisert på transaksjoner, selskapsstrukturering og kommersiell rådgivning for vekstselskaper.',
    },
  ];

  return (
    <section
      id="team"
      ref={sectionRef}
      style={{
        padding: 'clamp(80px, 12vh, 120px) clamp(24px, 5vw, 80px)',
        background: '#000',
        borderTop: '1px solid rgba(255,255,255,0.04)',
      }}
    >
      <div style={{
        maxWidth: '1100px',
        margin: '0 auto',
      }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          style={{ marginBottom: '60px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
            <div style={{ width: '40px', height: '1px', background: 'rgba(255,255,255,0.3)' }} />
            <span style={{
              fontSize: '11px',
              fontWeight: '500',
              color: 'rgba(255,255,255,0.4)',
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
            }}>
              Teamet
            </span>
          </div>

          <h2 style={{
            fontSize: 'clamp(32px, 5vw, 48px)',
            fontWeight: '200',
            color: '#fff',
            letterSpacing: '-0.03em',
            lineHeight: 1.1,
            marginBottom: '16px',
          }}>
            Hvem står bak<br />
            <span style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.4)' }}>Autonomous?</span>
          </h2>

          <p style={{
            fontSize: 'clamp(14px, 1.3vw, 16px)',
            color: 'rgba(255,255,255,0.4)',
            lineHeight: 1.7,
            maxWidth: '550px',
          }}>
            Vi er erfarne forretningsfolk som kombinerer dyp teknologiforståelse 
            med praktisk erfaring fra å bygge og skalere selskaper.
          </p>
        </motion.div>

        {/* Team Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '32px',
        }} className="team-cards">
          {team.map((member, i) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 + i * 0.15 }}
              style={{
                padding: '32px',
                background: 'rgba(255,255,255,0.02)',
                borderRadius: '20px',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              {/* Header with image */}
              <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '2px solid rgba(255,255,255,0.1)',
                  flexShrink: 0,
                }}>
                  <img 
                    src={member.image} 
                    alt={member.name}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                    }} 
                  />
                </div>
                <div style={{ paddingTop: '8px' }}>
                  <div style={{ 
                    fontSize: '18px', 
                    color: '#fff', 
                    fontWeight: '500', 
                    marginBottom: '4px',
                  }}>
                    {member.name}
                  </div>
                  <div style={{ 
                    fontSize: '13px', 
                    color: 'rgba(255,255,255,0.4)', 
                    marginBottom: '10px',
                  }}>
                    {member.role}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {member.tags.map((tag) => (
                      <span 
                        key={tag}
                        style={{
                          fontSize: '10px',
                          color: 'rgba(255,255,255,0.5)',
                          padding: '4px 10px',
                          background: 'rgba(255,255,255,0.05)',
                          borderRadius: '4px',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bio */}
              <p style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.5)',
                lineHeight: 1.7,
                margin: 0,
              }}>
                {member.bio}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// CONTACT SECTION - WORLD CLASS WITH FORM
// ════════════════════════════════════════════════════════════════════════════════
function ContactSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-50px' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const utmParams = getUTMParams();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          ...utmParams,
          page_url: window.location.href,
          referrer: document.referrer,
        }),
      });
      
      // Always read as text first, then parse
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        console.error('Failed to parse response:', text);
        throw new Error('Serverfeil. Prøv igjen senere.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Noe gikk galt');
      }
      
      // Track conversion
      trackLead({ ...formData, ...utmParams });
      trackEvent('Lead');
      
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Kunne ikke sende melding. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '16px 18px',
    fontSize: '15px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '12px',
    color: '#fff',
    outline: 'none',
    transition: 'border-color 0.2s, background 0.2s',
    fontFamily: 'inherit',
  };

  return (
    <section
      id="kontakt"
      ref={sectionRef}
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: 'clamp(80px, 15vh, 140px) clamp(24px, 5vw, 80px)',
        background: '#000',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background subtle grid */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: `
          linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px',
        opacity: 0.5,
        pointerEvents: 'none',
      }} />

      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        width: '100%',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Top section with label */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          style={{ marginBottom: '60px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
            <div style={{ width: '40px', height: '1px', background: 'rgba(255,255,255,0.3)' }} />
            <span style={{
              fontSize: '11px',
              fontWeight: '500',
              color: 'rgba(255,255,255,0.4)',
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
            }}>
              Kontakt Oss
            </span>
          </div>
        </motion.div>

        {/* Main content grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr',
          gap: 'clamp(40px, 8vw, 80px)',
          alignItems: 'start',
        }} className="contact-grid">
          
          {/* Left - Headline and form */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.1 }}
          >
            <h2 style={{
              fontSize: 'clamp(40px, 6vw, 72px)',
              fontWeight: '200',
              color: '#fff',
              letterSpacing: '-0.04em',
              lineHeight: 1,
              marginBottom: '24px',
            }}>
              La oss skape<br />
              <span style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.4)' }}>noe stort.</span>
            </h2>

            <p style={{
              fontSize: 'clamp(14px, 1.3vw, 16px)',
              color: 'rgba(255,255,255,0.4)',
              lineHeight: 1.7,
              maxWidth: '480px',
              marginBottom: '40px',
            }}>
              Vi er alltid på jakt etter spennende prosjekter og samarbeid. 
              Send oss en melding, så tar vi kontakt innen kort tid.
            </p>

            {/* Contact Form */}
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  style={{
                    padding: '40px',
                    background: 'rgba(255,255,255,0.02)',
                    borderRadius: '20px',
                    border: '1px solid rgba(255,255,255,0.08)',
                    textAlign: 'center',
                  }}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', delay: 0.1 }}
                    style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      background: 'rgba(34, 197, 94, 0.1)',
                      border: '2px solid rgba(34, 197, 94, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px',
                    }}
                  >
                    <Check size={32} color="#22c55e" />
                  </motion.div>
                  <h3 style={{ fontSize: '22px', color: '#fff', marginBottom: '8px', fontWeight: '400' }}>
                    Takk for din henvendelse!
                  </h3>
                  <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.5)' }}>
                    Vi tar kontakt innen kort tid.
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit}
                  exit={{ opacity: 0, scale: 0.95 }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    maxWidth: '500px',
                  }}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <input
                      type="text"
                      placeholder="Navn *"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'rgba(255,255,255,0.2)';
                        e.target.style.background = 'rgba(255,255,255,0.05)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'rgba(255,255,255,0.08)';
                        e.target.style.background = 'rgba(255,255,255,0.03)';
                      }}
                    />
                    <input
                      type="tel"
                      placeholder="Telefon"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'rgba(255,255,255,0.2)';
                        e.target.style.background = 'rgba(255,255,255,0.05)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'rgba(255,255,255,0.08)';
                        e.target.style.background = 'rgba(255,255,255,0.03)';
                      }}
                    />
                  </div>
                  
                  <input
                    type="email"
                    placeholder="E-post *"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'rgba(255,255,255,0.2)';
                      e.target.style.background = 'rgba(255,255,255,0.05)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(255,255,255,0.08)';
                      e.target.style.background = 'rgba(255,255,255,0.03)';
                    }}
                  />
                  
                  <textarea
                    placeholder="Fortell oss om prosjektet ditt..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={4}
                    style={{
                      ...inputStyle,
                      resize: 'vertical',
                      minHeight: '120px',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'rgba(255,255,255,0.2)';
                      e.target.style.background = 'rgba(255,255,255,0.05)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(255,255,255,0.08)';
                      e.target.style.background = 'rgba(255,255,255,0.03)';
                    }}
                  />

                  {error && (
                    <p style={{ color: '#ff6b6b', fontSize: '14px', margin: 0 }}>{error}</p>
                  )}

                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={{ scale: loading ? 1 : 1.02 }}
                    whileTap={{ scale: loading ? 1 : 0.98 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      padding: '18px 32px',
                      background: '#fff',
                      color: '#000',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '15px',
                      fontWeight: '500',
                      cursor: loading ? 'wait' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                      transition: 'opacity 0.2s',
                      marginTop: '8px',
                    }}
                  >
                    {loading ? (
                      'Sender...'
                    ) : (
                      <>
                        <Send size={18} />
                        Send melding
                      </>
                    )}
                  </motion.button>

                  <p style={{
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.3)',
                    textAlign: 'center',
                    marginTop: '8px',
                  }}>
                    Ved å sende inn godtar du at vi kontakter deg. Ingen spam.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Right - Info cards */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.2 }}
            style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
          >
            {/* Email direct */}
            <div style={{
              padding: '28px',
              background: 'rgba(255,255,255,0.02)',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.06)',
            }}>
              <p style={{ 
                fontSize: '11px', 
                color: 'rgba(255,255,255,0.35)', 
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                marginBottom: '12px',
              }}>
                E-post
              </p>
              <a 
                href="mailto:hei@autonomous.no"
                style={{ 
                  fontSize: '18px', 
                  color: '#fff', 
                  fontWeight: '400',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Mail size={18} style={{ opacity: 0.6 }} />
                hei@autonomous.no
              </a>
            </div>

            {/* Location */}
            <div style={{
              padding: '28px',
              background: 'rgba(255,255,255,0.02)',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.06)',
            }}>
              <p style={{ 
                fontSize: '11px', 
                color: 'rgba(255,255,255,0.35)', 
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                marginBottom: '12px',
              }}>
                Lokasjon
              </p>
              <p style={{ fontSize: '18px', color: '#fff', fontWeight: '400' }}>
                Bergen, Norge
              </p>
            </div>

            {/* Response time */}
            <div style={{
              padding: '28px',
              background: 'rgba(255,255,255,0.02)',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.06)',
            }}>
              <p style={{ 
                fontSize: '11px', 
                color: 'rgba(255,255,255,0.35)', 
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                marginBottom: '12px',
              }}>
                Responstid
              </p>
              <p style={{ fontSize: '18px', color: '#fff', fontWeight: '400' }}>
                Innen kort tid
              </p>
            </div>

            {/* Availability */}
            <div style={{
              padding: '28px',
              background: 'rgba(255,255,255,0.02)',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.06)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <motion.div
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#22c55e',
                  }}
                />
                <p style={{ 
                  fontSize: '11px', 
                  color: 'rgba(255,255,255,0.35)', 
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                }}>
                  Tilgjengelighet
                </p>
              </div>
              <p style={{ fontSize: '18px', color: '#fff', fontWeight: '400', marginTop: '12px' }}>
                Åpen for nye prosjekter
              </p>
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          style={{
            marginTop: 'clamp(80px, 12vh, 120px)',
            paddingTop: '40px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '20px',
          }}
        >
          <p style={{
            fontSize: '13px',
            color: 'rgba(255,255,255,0.3)',
          }}>
            © 2025 Autonomous
          </p>

          <div style={{ display: 'flex', gap: '32px' }}>
            {['LinkedIn', 'GitHub', 'Twitter'].map((social) => (
              <a
                key={social}
                href="#"
                style={{
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.4)',
                  textDecoration: 'none',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.4)'}
              >
                {social}
              </a>
            ))}
          </div>

          <p style={{
            fontSize: '13px',
            color: 'rgba(255,255,255,0.3)',
          }}>
            Bergen, Norge
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// RESPONSIVE STYLES
// ════════════════════════════════════════════════════════════════════════════════
const responsiveStyles = `
  /* Desktop nav visible, mobile hidden */
  .desktop-nav { display: flex !important; }
  .mobile-menu-btn { display: none !important; }
  .mobile-nav-overlay { display: none !important; }
  
  /* Services 2-column on desktop */
  .services-container {
    grid-template-columns: 1fr 1.5fr !important;
  }
  
  /* Video 2-column on desktop - already set in inline styles */
  .video-grid {
    grid-template-columns: 1fr 1fr !important;
  }
  
  /* Contact grid on desktop */
  .contact-grid {
    grid-template-columns: 1.5fr 1fr !important;
  }
  
  /* Active indicator visible on desktop */
  .active-indicator { display: block !important; }
  
  /* Tablet: 768px - 1024px */
  @media (max-width: 1024px) {
    .services-container {
      grid-template-columns: 1fr 1.2fr !important;
      gap: 30px !important;
    }
    .video-grid {
      grid-template-columns: 1fr 1fr !important;
      gap: 40px !important;
    }
    .contact-grid {
      grid-template-columns: 1fr !important;
      gap: 48px !important;
    }
  }
  
  /* Mobile: under 768px */
  @media (max-width: 768px) {
    .desktop-nav { display: none !important; }
    .mobile-menu-btn { display: block !important; }
    
    .services-container {
      grid-template-columns: 1fr !important;
      gap: 32px !important;
    }
    
    .preview-container {
      height: 500px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      padding: 20px !important;
    }
    
    .video-grid {
      grid-template-columns: 1fr !important;
      gap: 40px !important;
    }
    
    .contact-grid {
      grid-template-columns: 1fr !important;
      gap: 40px !important;
    }
    
    .active-indicator { display: none !important; }
  }
  
  /* Small mobile: under 480px */
  @media (max-width: 480px) {
    .preview-container {
      height: 480px !important;
      border-radius: 16px !important;
      padding: 16px !important;
    }
  }
`;

// ════════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════════
export default function MinimalistSite() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700&display=swap');
        
        html, body { 
          background: #000 !important; 
          margin: 0;
          padding: 0;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        * {
          box-sizing: border-box;
        }
        ::selection {
          background: rgba(255,255,255,0.2);
          color: #fff;
        }
        html {
          scroll-behavior: smooth;
        }
        
        /* Fix for Safari thin font rendering - AGGRESSIVE */
        .logo-text {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif !important;
          font-weight: 100 !important;
          -webkit-font-smoothing: antialiased !important;
          -moz-osx-font-smoothing: grayscale !important;
          font-synthesis: none !important;
          text-rendering: geometricPrecision !important;
          font-feature-settings: 'kern' 1 !important;
          font-optical-sizing: none !important;
        }
        
        /* Safari-specific fix */
        @supports (-webkit-backdrop-filter: none) {
          .logo-text {
            font-weight: 100 !important;
            -webkit-text-stroke: 0px !important;
            letter-spacing: -0.06em !important;
          }
        }
        
        ${responsiveStyles}
      `}</style>

      <motion.div
        data-page="minimalist"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        style={{
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          background: '#000',
          color: '#fff',
          minHeight: '100vh',
          overflowX: 'hidden',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        }}
      >
        <Navigation />
        <HeroSection />
        <ServicesSection />
        <VideoSection />
        <TechSection />
        <TeamSection />
        <ContactSection />
      </motion.div>
    </>
  );
}
